<script setup lang="ts">
import { useProgramEnrollments } from '@/composables/eos/program-enrollments'
import type {
  ICreateOptOutPeriodBody,
  IEditProgramEnrollmentBody,
  IProgramEnrollment,
} from '@/types/eos/ProgramEnrollment'
import { computed, ref } from 'vue'
import ProgramEnrollmentForm, { type ProgramEnrollmentFormValue } from './ProgramEnrollmentForm.vue'

// Being el-dialog a teleported element, :deep() selectors in a scoped
// <style> block have nothing to anchor to and never match.
// Thus we add a plain <div> wrapper that becomes the true component root.
// inheritAttrs:false + v-bind="$attrs" on el-dialog keeps v-model and other parent bindings working.
defineOptions({ inheritAttrs: false })

const props = defineProps<{
  existingEnrollments: IProgramEnrollment[] | null
}>()

const emit = defineEmits<{ created: [] }>()

// Dialog visibility. Declared as a model (rather than passed through $attrs) so the
// Cancel button can close the dialog directly.
const visible = defineModel<boolean>()

const formRef = ref<InstanceType<typeof ProgramEnrollmentForm>>()
const formValue = ref<ProgramEnrollmentFormValue>({ enrollments: [] })

const {
  createProgramEnrollmentsMutation,
  editProgramEnrollmentMutation,
  deleteProgramEnrollmentMutation,
  createOptOutPeriodMutation,
  deleteOptOutPeriodMutation,
} = useProgramEnrollments()
const { mutate: createEnrollments, isPending: isCreatePending } = createProgramEnrollmentsMutation
const { mutate: editEnrollment, isPending: isEditPending } = editProgramEnrollmentMutation
const { mutate: deleteEnrollment, isPending: isDeletePending } = deleteProgramEnrollmentMutation
const { mutate: createOptOutPeriod, isPending: isCreateOptOutPending } = createOptOutPeriodMutation
const { mutate: deleteOptOutPeriod, isPending: isDeleteOptOutPending } = deleteOptOutPeriodMutation

const isMutating = computed(
  () =>
    isCreatePending.value ||
    isEditPending.value ||
    isDeletePending.value ||
    isCreateOptOutPending.value ||
    isDeleteOptOutPending.value
)

const canEnroll = computed(() => formRef.value?.isValid && !isMutating.value)

function onEditExisting({
  id,
  body,
  onSuccess,
}: {
  id: number
  body: IEditProgramEnrollmentBody
  onSuccess: () => void
}) {
  editEnrollment({ id, body }, { onSuccess })
}

function onDeleteExisting({ id, onSuccess }: { id: number; onSuccess: () => void }) {
  deleteEnrollment(id, { onSuccess })
}

function onCreateOptOutPeriod({
  programEnrollmentId,
  body,
  onSuccess,
}: {
  programEnrollmentId: number
  body: ICreateOptOutPeriodBody
  onSuccess: () => void
}) {
  createOptOutPeriod({ programEnrollmentId, body }, { onSuccess })
}

function onDeleteOptOutPeriod({
  programEnrollmentId,
  optOutPeriodId,
  onSuccess,
  onError,
}: {
  programEnrollmentId: number
  optOutPeriodId: number
  onSuccess: () => void
  onError: (err: unknown) => void
}) {
  deleteOptOutPeriod({ programEnrollmentId, optOutPeriodId }, { onSuccess, onError })
}

function clearState() {
  formValue.value = { enrollments: [] }
}

/** Pre-populates the top select controls: site and ISO, which are assumed to be the same of every item. */
function onOpen() {
  if (props.existingEnrollments?.length) {
    const first = props.existingEnrollments[0]
    formValue.value = {
      site: { site_id: first.site.id, site_name: first.site.name },
      iso: first.program.iso,
      enrollments: [],
    }
  } else {
    clearState()
  }
}

function onEnroll() {
  if (!formRef.value?.isValid || !formValue.value.site) return

  createEnrollments(
    {
      site_id: formValue.value.site.site_id!,
      program_enrollments: formValue.value.enrollments.map((e) => ({
        program_id: e.program!.id,
        site_split: parseInt(e.site_split!, 10),
        start_enrollment_period_timestamp: e.start_enrollment_period_timestamp!,
        end_enrollment_period_timestamp: e.doesntEnd ? null : e.end_enrollment_period_timestamp!,
      })),
    },
    {
      onSuccess() {
        clearState()
        emit('created')
      },
    }
  )
}
</script>

<template>
  <div>
    <el-dialog
      v-model="visible"
      v-bind="$attrs"
      title="Program Enrollment"
      width="544px"
      @open="onOpen"
      @closed="formRef?.resetTransientState()"
    >
      <ProgramEnrollmentForm
        ref="formRef"
        v-model="formValue"
        :disabled="isMutating"
        :existing-enrollments="existingEnrollments"
        @delete-existing="onDeleteExisting"
        @edit-existing="onEditExisting"
        @create-opt-out-period="onCreateOptOutPeriod"
        @delete-opt-out-period="onDeleteOptOutPeriod"
      />
      <template #footer>
        <div class="flex justify-end gap-2">
          <el-button size="large" :disabled="isMutating" @click="visible = false">Cancel</el-button>
          <el-button type="primary" size="large" :disabled="!canEnroll" :loading="isCreatePending" @click="onEnroll"
            >Submit</el-button
          >
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-dialog;

  // Tighten the footer inset to spacing-md (the shared mixin uses 16px 24px).
  .el-dialog__footer {
    padding: var(--spacing-spacing-md);
  }

  .el-form-item {
    width: 100%;
    margin-bottom: 0;
  }

  .el-date-editor .el-input__prefix {
    display: none;
  }

  .el-card {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border: none;
      color: var(--text-secondary);
      padding-top: 12px;
      padding-bottom: 0;
    }
  }
}
</style>
